import React, { useCallback, useRef, useState } from "react";
import { MessageCircle, Pencil, Trash2, Check, X } from "lucide-react";
import type { ChatSession } from "./types";

interface ChatSessionItemProps {
  session: ChatSession;
  selected: boolean;
  onClick: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}

export const ChatSessionItem: React.FC<ChatSessionItemProps> = ({
  session,
  selected,
  onClick,
  onRename,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(session.title);
  const cancelledRef = useRef(false);

  const commitRename = useCallback(() => {
    if (cancelledRef.current) return;
    const trimmed = draft.trim();
    if (trimmed && trimmed !== session.title) {
      onRename(trimmed);
    }
    setEditing(false);
  }, [draft, session.title, onRename]);

  const cancelRename = useCallback(() => {
    cancelledRef.current = true;
    setDraft(session.title);
    setEditing(false);
    setTimeout(() => {
      cancelledRef.current = false;
    }, 0);
  }, [session.title]);

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-muted">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") cancelRename();
          }}
          onBlur={commitRename}
          className="flex-1 min-w-0 text-xs bg-transparent outline-none text-foreground"
          autoFocus
        />
        <button onClick={commitRename} className="p-0.5 hover:text-primary" aria-label="Save">
          <Check className="size-3" />
        </button>
        <button onClick={cancelRename} className="p-0.5 hover:text-destructive" aria-label="Cancel">
          <X className="size-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors text-xs ${
        selected
          ? "bg-primary/10 text-primary font-medium"
          : "text-foreground hover:bg-muted"
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick();
      }}
    >
      <MessageCircle className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">{session.title || "Untitled"}</span>
      <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          className="p-0.5 rounded hover:bg-background"
          aria-label="Rename"
        >
          <Pencil className="size-3 text-muted-foreground" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-0.5 rounded hover:bg-background"
          aria-label="Delete"
        >
          <Trash2 className="size-3 text-muted-foreground hover:text-destructive" />
        </button>
      </div>
    </div>
  );
};
